// src/contexts/LocalizationContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '../locales';

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

export const LocalizationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);

  // Available languages with their properties
  const languages = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      flag: 'gb',
      rtl: false
    },
    {
      code: 'si',
      name: 'Sinhala',
      nativeName: 'සිංහල',
      flag: 'lk',
      rtl: false
    },
    {
      code: 'ta',
      name: 'Tamil',
      nativeName: 'தமிழ்',
      flag: 'lk',
      rtl: false
    }
  ];

  // Initialize language from localStorage or browser preference
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const browserLanguage = navigator.language.split('-')[0];
    
    let initialLanguage = 'en'; // Default fallback
    
    if (savedLanguage && languages.find(lang => lang.code === savedLanguage)) {
      initialLanguage = savedLanguage;
    } else if (languages.find(lang => lang.code === browserLanguage)) {
      initialLanguage = browserLanguage;
    }
    
    setCurrentLanguage(initialLanguage);
    
    // Set RTL based on language
    const selectedLang = languages.find(lang => lang.code === initialLanguage);
    setIsRTL(selectedLang?.rtl || false);
    
    // Update document direction
    document.documentElement.dir = selectedLang?.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = initialLanguage;
  }, []);

  // Change language function
  const changeLanguage = (languageCode) => {
    if (languages.find(lang => lang.code === languageCode)) {
      setCurrentLanguage(languageCode);
      localStorage.setItem('language', languageCode);
      
      // Update RTL setting
      const selectedLang = languages.find(lang => lang.code === languageCode);
      const isRightToLeft = selectedLang?.rtl || false;
      setIsRTL(isRightToLeft);
      
      // Update document direction and language
      document.documentElement.dir = isRightToLeft ? 'rtl' : 'ltr';
      document.documentElement.lang = languageCode;
      
      // Dispatch custom event for components that need to react to language changes
      window.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { language: languageCode, isRTL: isRightToLeft } 
      }));
    }
  };

  const t = (key, params = {}) => {
    const fallback = typeof params === 'string' ? params : undefined;
    const interpolationParams = typeof params === 'string' ? {} : params;

    const keys = key.split('.');
    let translation = translations[currentLanguage];

    // Navigate through nested keys
    for (const k of keys) {
      if (translation && typeof translation === 'object' && k in translation) {
        translation = translation[k];
      } else {
        // Fallback to English if translation not found
        translation = translations.en;
        for (const fallbackKey of keys) {
          if (translation && typeof translation === 'object' && fallbackKey in translation) {
            translation = translation[fallbackKey];
          } else {
            if (fallback === undefined) {
              console.warn(`Translation missing for key: ${key} in language: ${currentLanguage}`);
            }
            return fallback !== undefined ? fallback : key; // Return the fallback, or the key itself, if no translation found
          }
        }
        break;
      }
    }

    // Handle string interpolation — supports both {param} and {{param}} placeholders
    // (several locale files use the double-brace form).
    if (typeof translation === 'string' && Object.keys(interpolationParams).length > 0) {
      return translation.replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, (match, doubleKey, singleKey) => {
        return interpolationParams[doubleKey || singleKey] ?? match;
      });
    }

    return translation || fallback || key;
  };

  // Get current language info
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0];
  };

  // Format numbers according to locale
  const formatNumber = (number, options = {}) => {
    const localeMap = {
      'en': 'en-US',
      'si': 'si-LK',
      'ta': 'ta-LK'
    };
    
    const locale = localeMap[currentLanguage] || 'en-US';
    return new Intl.NumberFormat(locale, options).format(number);
  };

  // Format currency
  const formatCurrency = (amount, currency = 'LKR') => {
    return formatNumber(amount, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    });
  };

  // Format date
  const formatDate = (date, options = {}) => {
    const localeMap = {
      'en': 'en-US',
      'si': 'si-LK',
      'ta': 'ta-LK'
    };
    
    const locale = localeMap[currentLanguage] || 'en-US';
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(new Date(date));
  };

  const value = {
    currentLanguage,
    languages,
    isRTL,
    changeLanguage,
    t,
    getCurrentLanguage,
    formatNumber,
    formatCurrency,
    formatDate
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};